import {
  IConfigApp,
  IExternalPlugin,
  IPluginConfig,
  IPluginFieldHandler,
  IPluginSettingPage,
  PluginCore,
  Project,
  registerPlugin,
} from "matrix-requirements-sdk/client";
import { sdkInstance } from "./Instance";
import { DashboardPage, IDashboardParameters } from "./Dashboard/DashboardPage";
import { ServerSettingsPage } from "./ServerSettingsPage/ServerSettingsPage";
import {
  IPluginFieldValue,
  IProjectSettings,
  IServerSettings,
} from "./Interfaces";

import "../CsatNpcDialog";

/** This class is allows you to configure the features of your plugin.
 *
 *  You can also implement functions to into the plugin (at start in the constructor, when loading a project, when loading an item)
 *
 */
export class Plugin
  implements
    IExternalPlugin<
      IServerSettings,
      IProjectSettings,
      IPluginFieldHandler<IPluginFieldValue>,
      IPluginFieldValue,
      IDashboardParameters
    >
{
  /**This part enables which
   *
   * See IPluginConfig interface for explanation of parameters
   */

  static config: IPluginConfig<IServerSettings, IProjectSettings> = {
    /*  Page in admin client to configure settings across all projects - set enabled to false if not needed.
            The page itself is implemented in the _ServerSettingsPage.ts
        */
    customerSettingsPage: {
      id: "NPS_CSATCustomerSettings",
      title: "NPS and CSAT",
      type: "NPS_CSATcs",
      enabled: true,
      defaultSettings: {
        daysBeetweenFeedbacks: 180,
        daysAfterClosedPopup: 7,
        timeOutBeforePopup: 20000,
      },
      settingName: "NPS_CSAT_settings",
      help: "This page displays NPS and CSAT and  feedback from our customers. We appreciate your feedback and use it to improve our products and services.",
      helpUrl: "https://docs23.matrixreq.com",
    },
    /*  Page in admin client to configure settings for one specific project - set enabled to false if not needed.
            The page itself is implemented in the _ProjectSetingsPage.ts
        */
    projectSettingsPage: {
      id: "NPS_CSATprojectsettings",
      title: "NPS_CSAT projectsettings page",
      type: "NPS_CSATps",
      enabled: false,
      defaultSettings: {
        myProjectSetting: "default value for setting defined in Interfaces.ts",
      },
      settingName: "NPS_CSAT_settings",
      help: "",
      helpUrl: "https://docs23.matrixreq.com",
    },
    /*  Add an entry in the tool menu of an item or folder - set enabled to false if not needed.
            The tool itself is implemented in the _Tool.ts
        */
    menuToolItem: {
      enabled: false,
      title: "ui_plugin_csat_nps-menuitem",
    },
    /*  Add a custom field to enter some data in the UI - set enabled to false if not needed.
            The field itself is implemented in the _Control.ts
        */
    field: {
      enabled: false,
      fieldType: "ui_plugin_csat_nps",
      title: "ui_plugin_csat_nps-field",
      fieldConfigOptions: {
        id: "ui_plugin_csat_nps",
        capabilities: {
          canBePublished: false,
          canBeReadonly: true,
          canBeXtcPreset: false,
          canHideInDoc: false,
          canBeUsedInDocs: false,
          canRequireContent: true,
        },
        class: "",
        help: "",
        label: "ui_plugin_csat_nps-field",
      },
    },
    /*  Add a dashboard inside a project - set enabled to false if not needed.
            The field itself is implemented in the _Control.ts
        */
    dashboard: {
      id: "NPS_CSAT",
      title: "NPS/CSAT dashboard page",
      enabled: false,
      icon: "fal fa-cog",
      parent: "DASHBOARDS",
      usefilter: true,
      order: 9999,
    },
  };
  core: PluginCore;
  PLUGIN_VERSION = "1.0.0";
  PLUGIN_NAME = "NPS and CSAT";
  private currentProject: Project;

  constructor() {
    this.core = new sdkInstance.PluginCore(this);
    this.currentProject = null;
  }

  async getDashboardAsync(): Promise<DashboardPage> {
    await this.setupProject();
    return new DashboardPage(
      this.currentProject,
      sdkInstance.globalMatrix.projectStorage,
    );
  }

  async getProjectSettingsPageAsync(): Promise<
    IPluginSettingPage<IProjectSettings>
  > {
    return null;
  }

  async getServerSettingsPageAsync(): Promise<
    IPluginSettingPage<IServerSettings>
  > {
    if (sdkInstance.app.isConfigApp()) {
      return new ServerSettingsPage(<IConfigApp>(<unknown>sdkInstance.app));
    }
    return null;
  }

  async getControlAsync() {
    return null;
  }

  async getToolAsync() {
    return null;
  }

  getConfig() {
    return Plugin.config;
  }

  enableToolMenu(): boolean {
    return false;
  }

  /**
   * This method is called each time  a project has been loaded and initialized.
   * At the time it is called, all project settings, categories etc are defined.
   *
   * @param project // id of the loaded project
   */
  onInitProject(project: string) {
    // here is a good place to decide based on the project (maybe some project setting), whether the plugin should be enabled

    // if not:
    // this.enabledInContext = false;
    this.setupProject(project);
  }

  onInitItem() {
    // here is a good place to decide based on the selection in the tree, whether the plugin should be enabled
    // if not:
    // this.enabledInContext = false;
  }

  private async setupProject(newProjectName?: string) {
    if (this.currentProject) {
      // Did we change projects?
      if (newProjectName && this.currentProject.getName() != newProjectName) {
        this.currentProject = null;
      }
    }
    if (this.currentProject == null) {
      this.currentProject = newProjectName
        ? await sdkInstance.matrixsdk.openProject(newProjectName)
        : await sdkInstance.matrixsdk.openCurrentProjectFromSession();
    }
  }
}

registerPlugin(new Plugin().core);

const initReactComponent = () => {
  const event = new Event("initReact");
  document.dispatchEvent(event);
};

const createAndMountDiv = () => {
  document.addEventListener("DOMContentLoaded", () => {
    const appContainer = document.createElement("div");
    appContainer.id = "csat-npc-dialog";
    appContainer.style.width = "100%";
    appContainer.style.height = "100%";

    document.body.appendChild(appContainer);

    initReactComponent();
  });
};

createAndMountDiv();
